import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ViewStyle,
} from 'react-native';
import { useAppSelector } from '@/store';
import { useLinkedAccountsColors } from '@/constants/residents/mobile/linkedAccountsColors';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { newInvitationStrings, formatString } from '@/constants/residents/strings';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { InfoBubbleModal } from './InfoBubbleModal';

const MailIcon = SVG_COMPONENTS.EMAIL_SOLID;
const InfoIcon = RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE;
const WarningIcon = RESIDENTS_SVG_COMPONENTS.WARNING;

const MAX_WORDS = 300;
const SPECIAL_CHARS_REGEX = /[<>$#%/\\]/;

interface NewInvitationModalProps {
  visible: boolean;
  onClose: () => void;
  onSend: (email: string, message: string) => Promise<void>;
}

interface ValidationState {
  wordLimitExceeded: boolean;
  hasSpecialChars: boolean;
  emailInvalid: boolean;
  emailEmpty: boolean;
  showHardErrors: boolean;
}

export const NewInvitationModal: React.FC<NewInvitationModalProps> = ({
  visible,
  onClose,
  onSend,
}) => {
  const LINKED_ACCOUNTS_COLORS = useLinkedAccountsColors();
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const { user } = useAppSelector((state) => state.auth);

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState('');
  const [showInfoBubble, setShowInfoBubble] = useState(false);
  const [validation, setValidation] = useState<ValidationState>({
    wordLimitExceeded: false,
    hasSpecialChars: false,
    emailInvalid: false,
    emailEmpty: true,
    showHardErrors: false,
  });

  const defaultMessage = useMemo(() => {
    const userName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.email || 'User';

    return formatString(newInvitationStrings.messageFormatted, userName);
  }, [user]);

  useEffect(() => {
    if (visible) {
      setMessage(defaultMessage);
      setEmail('');
      setValidation({
        wordLimitExceeded: false,
        hasSpecialChars: false,
        emailInvalid: false,
        emailEmpty: true,
        showHardErrors: false,
      });
      setIsLoading(false);
      setLoadingEmail('');
      setShowInfoBubble(false);
    }
  }, [visible, defaultMessage]);

  const wordCount = useMemo(() => {
    const trimmed = message.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).length;
  }, [message]);

  const hasSpecialChars = useMemo(() => {
    return SPECIAL_CHARS_REGEX.test(message);
  }, [message]);

  const wordLimitExceeded = useMemo(() => {
    return wordCount > MAX_WORDS;
  }, [wordCount]);

  const validateEmail = useCallback((text: string) => {
    const trimmed = text.trim();
    const emailEmpty = !trimmed;
    const emailInvalid = !emailEmpty && !trimmed.includes('@');

    setValidation(prev => ({
      ...prev,
      emailEmpty,
      emailInvalid,
    }));

    return { emailEmpty, emailInvalid };
  }, []);

  const handleMessageChange = useCallback((text: string) => {
    setMessage(text);
  }, []);

  const handleEmailChange = useCallback((text: string) => {
    setEmail(text);
    validateEmail(text);
  }, [validateEmail]);

  const showMessageWarning = wordLimitExceeded || hasSpecialChars;

  const handleSend = useCallback(async () => {
    const emailValidation = validateEmail(email);

    const hasErrors =
      emailValidation.emailEmpty ||
      emailValidation.emailInvalid ||
      wordLimitExceeded ||
      hasSpecialChars;

    if (hasErrors) {
      setValidation(prev => ({ ...prev, showHardErrors: true }));
      return;
    }

    setLoadingEmail(email.trim());
    setIsLoading(true);

    try {
      await onSend(email.trim(), message.trim());
    } finally {
      setIsLoading(false);
      setLoadingEmail('');
    }
  }, [email, message, onSend, validateEmail, wordLimitExceeded, hasSpecialChars]);

  const messageErrors = useMemo(() => {
    const errors: string[] = [];
    if (wordLimitExceeded) {
      errors.push(formatString(newInvitationStrings.validation.wordLimitExceeded, MAX_WORDS, wordCount));
    }
    if (hasSpecialChars) {
      errors.push(newInvitationStrings.validation.noSpecialChars);
    }
    return errors;
  }, [wordLimitExceeded, hasSpecialChars, wordCount]);

  const emailErrors = useMemo(() => {
    if (validation.showHardErrors) {
      if (validation.emailEmpty) return newInvitationStrings.validation.emailRequired;
      if (validation.emailInvalid) return newInvitationStrings.validation.emailInvalid;
    }
    return '';
  }, [validation.showHardErrors, validation.emailEmpty, validation.emailInvalid]);

  const warningTextColor = useMemo(
    () => validation.showHardErrors ? LINKED_ACCOUNTS_COLORS.accentPink : LINKED_ACCOUNTS_COLORS.warningOrange,
    [validation.showHardErrors, LINKED_ACCOUNTS_COLORS.accentPink, LINKED_ACCOUNTS_COLORS.warningOrange]
  );

  const footerContent = (
    <View style={styles.footerRow}>
      <ModalButton
        label={newInvitationStrings.cancelButton}
        variant="secondary"
        onPress={onClose}
        disabled={isLoading}
      />
      <ModalButton
        label={newInvitationStrings.sendButton}
        variant="primary"
        onPress={handleSend}
        disabled={isLoading}
      />
    </View>
  );

  return (
    <>
      <UnifiedModal
        visible={visible}
        onClose={onClose}
        variant="fullscreen"
        title={newInvitationStrings.modalTitle}
        showHeader
        scrollable
        avoidKeyboard
        dismissOnBackdrop={false}
        footer={footerContent}
        contentContainerStyle={scrollContentOverride}
      >
        {/* Instruction Text */}
        <Text style={[styles.instructionText, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}>
          {newInvitationStrings.header}
        </Text>

        {/* Email Input */}
        <View style={styles.inputGroup}>
          <Text style={[styles.inputLabel, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}>{newInvitationStrings.emailLabel}</Text>
          <View style={[
            styles.inputContainer,
            { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder },
            validation.showHardErrors && (validation.emailEmpty || validation.emailInvalid) && { borderColor: LINKED_ACCOUNTS_COLORS.accentPink },
          ]}>
            <View style={styles.inputLeft}>
              <View style={styles.inputIconContainer}>
                <MailIcon width={20} height={20} color={LINKED_ACCOUNTS_COLORS.textSecondary} />
              </View>
              <TextInput
                style={[styles.emailInput, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}
                value={email}
                onChangeText={handleEmailChange}
                placeholder={newInvitationStrings.emailPlaceholder}
                placeholderTextColor={LINKED_ACCOUNTS_COLORS.textSecondary}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                editable={!isLoading}
              />
            </View>
            <View style={[styles.inputRight, { borderLeftColor: MARKETPLACE_COLORS.inputBorder }]} />
          </View>
          {emailErrors ? (
            <Text style={[styles.errorText, { color: LINKED_ACCOUNTS_COLORS.accentPink }]}>{emailErrors}</Text>
          ) : null}
        </View>

        {/* Message Input */}
        <View style={styles.inputGroup}>
          <Text style={[styles.inputLabel, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}>
            {newInvitationStrings.personalMessageLabel} <Text style={[styles.optionalText, { color: LINKED_ACCOUNTS_COLORS.textSecondary }]}>{newInvitationStrings.optionalText}</Text>
          </Text>
          <View style={[
            styles.textareaContainer,
            { backgroundColor: MARKETPLACE_COLORS.inputBackground, borderColor: MARKETPLACE_COLORS.inputBorder },
            showMessageWarning && !validation.showHardErrors && { borderColor: LINKED_ACCOUNTS_COLORS.warningOrange },
            validation.showHardErrors && showMessageWarning && { borderColor: LINKED_ACCOUNTS_COLORS.accentPink },
          ]}>
            <TextInput
              style={[styles.textareaInput, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}
              value={message}
              onChangeText={handleMessageChange}
              placeholder={newInvitationStrings.messagePlaceholder}
              placeholderTextColor={LINKED_ACCOUNTS_COLORS.textSecondary}
              multiline
              textAlignVertical="top"
              editable={!isLoading}
            />
          </View>
          {messageErrors.length > 0 && (
            <View style={styles.errorContainer}>
              {messageErrors.map((error, index) => (
                <View key={index} style={styles.errorRow}>
                  <WarningIcon
                    width={20}
                    height={20}
                    color={warningTextColor}
                  />
                  <Text style={[styles.warningText, { color: warningTextColor }]}>
                    {error}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Info Section */}
        <View style={styles.infoSection}>
          <Text style={[styles.disclaimerText, { color: LINKED_ACCOUNTS_COLORS.textSecondary }]}>
            {newInvitationStrings.disclaimer}
          </Text>

          <TouchableOpacity
            style={styles.infoLink}
            onPress={() => setShowInfoBubble(true)}
            activeOpacity={0.7}
            disabled={isLoading}
          >
            <Text style={[styles.infoLinkText, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}>{newInvitationStrings.howItWorks.item2.title}</Text>
            <InfoIcon width={20} height={20} color={LINKED_ACCOUNTS_COLORS.textPrimary} />
          </TouchableOpacity>
        </View>

        {isLoading && (
          <View style={[styles.loadingOverlay, { backgroundColor: LINKED_ACCOUNTS_COLORS.background }]}>
            <View style={styles.loadingContent}>
              <ActivityIndicator size="large" color={LINKED_ACCOUNTS_COLORS.textPrimary} />
              <Text style={[styles.loadingText, { color: LINKED_ACCOUNTS_COLORS.textPrimary }]}>
                {formatString(newInvitationStrings.loadingText, loadingEmail)}
              </Text>
            </View>
          </View>
        )}
      </UnifiedModal>

      <InfoBubbleModal
        visible={showInfoBubble}
        onClose={() => setShowInfoBubble(false)}
      />
    </>
  );
};

const scrollContentOverride: ViewStyle = { padding: 24, paddingBottom: 0, gap: 16 };

const styles = StyleSheet.create({
  instructionText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  inputGroup: {
    gap: 4,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  optionalText: {},
  inputContainer: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  inputLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
  },
  inputRight: {
    width: 52,
    borderLeftWidth: 1,
  },
  inputIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emailInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    padding: 0,
    lineHeight: 17.5,
  },
  textareaContainer: {
    borderWidth: 1,
    borderRadius: 8,
    minHeight: 180,
    overflow: 'hidden',
  },
  textareaInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    padding: 16,
    paddingTop: 11,
    lineHeight: 17.5,
  },
  errorContainer: {
    gap: 4,
    paddingTop: 4,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  warningText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
  errorText: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
  },
  infoSection: {
    gap: 16,
    paddingBottom: 24,
  },
  disclaimerText: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },
  infoLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  infoLinkText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 12,
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContent: {
    paddingHorizontal: 32,
    paddingVertical: 40,
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
  },
});
